import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api from "../services/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Switch } from "../components/ui/switch";
import {
  Loader2, Truck, Bike, Home, Luggage, Building2, Sofa,
  ArrowUp, ArrowDown, RotateCcw, Save, CheckCircle2, XCircle,
} from "lucide-react";

const ICON_MAP = { Bike, Home, Luggage, Building2, Sofa };

function ServiceIcon({ icon }) {
  const I = ICON_MAP[icon] || Truck;
  return <I className="h-5 w-5" />;
}

export default function AdminServiceTypes() {
  const [items, setItems] = useState([]);
  const [original, setOriginal] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/service-types");
      const list = (data?.service_types || []).map((s) => ({ ...s }));
      setItems(list);
      setOriginal(JSON.parse(JSON.stringify(list)));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load service types");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const dirty = JSON.stringify(items) !== JSON.stringify(original);

  const toggleEnabled = (id) => {
    setItems((prev) => prev.map((s) => (s.id === id ? { ...s, enabled: !s.enabled } : s)));
  };

  const renameLabel = (id, label) => {
    setItems((prev) => prev.map((s) => (s.id === id ? { ...s, label } : s)));
  };

  const move = (idx, dir) => {
    const next = [...items];
    const target = idx + dir;
    if (target < 0 || target >= next.length) return;
    [next[idx], next[target]] = [next[target], next[idx]];
    // re-stamp sort_order
    next.forEach((s, i) => { s.sort_order = i + 1; });
    setItems(next);
  };

  const save = async () => {
    // Guard: at least one must remain enabled, otherwise customers can't book anything.
    const enabledCount = items.filter((s) => s.enabled).length;
    if (enabledCount === 0) {
      toast.error("At least one service type must remain enabled.");
      return;
    }
    setSaving(true);
    try {
      const payload = { service_types: items.map((s, i) => ({ ...s, sort_order: i + 1 })) };
      const { data } = await api.put("/admin/service-types", payload);
      const list = (data?.service_types || []).map((s) => ({ ...s }));
      setItems(list);
      setOriginal(JSON.parse(JSON.stringify(list)));
      toast.success("Service types saved. Changes apply to new bookings instantly.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    if (!window.confirm("Reset all service types to defaults (all enabled, original order)?")) return;
    setSaving(true);
    try {
      const { data } = await api.post("/admin/service-types/reset");
      const list = (data?.service_types || []).map((s) => ({ ...s }));
      setItems(list);
      setOriginal(JSON.parse(JSON.stringify(list)));
      toast.success("Reset to defaults.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reset failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6 p-4 md:p-6 max-w-4xl" data-testid="admin-service-types-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900 flex items-center gap-2">
            <Truck className="h-6 w-6 text-[#FF6B00]" />
            Service Types
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Toggle service types on/off, rename their customer-facing label, and reorder them.
            Disabled types are hidden from the booking page; existing orders are unaffected.
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={reset}
            disabled={saving || loading}
            data-testid="reset-defaults-btn"
            className="text-slate-600"
          >
            <RotateCcw className="h-4 w-4 mr-1.5" />
            Reset
          </Button>
          <Button
            onClick={save}
            disabled={!dirty || saving || loading}
            data-testid="save-service-types-btn"
            className="bg-[#FF6B00] hover:bg-[#FF6B00]/90 text-white"
          >
            {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />}
            Save changes
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-slate-200 divide-y divide-slate-100" data-testid="service-types-list">
          {items.map((s, idx) => (
            <div
              key={s.id}
              className={`flex items-center gap-3 p-4 transition-colors ${s.enabled ? "" : "bg-slate-50/60"}`}
              data-testid={`service-type-row-${s.id}`}
            >
              {/* Reorder controls */}
              <div className="flex flex-col gap-0.5">
                <button
                  type="button"
                  onClick={() => move(idx, -1)}
                  disabled={idx === 0}
                  className="p-1 rounded hover:bg-slate-100 text-slate-400 disabled:opacity-30 disabled:cursor-not-allowed"
                  aria-label="Move up"
                  data-testid={`move-up-${s.id}`}
                >
                  <ArrowUp className="h-3.5 w-3.5" />
                </button>
                <button
                  type="button"
                  onClick={() => move(idx, 1)}
                  disabled={idx === items.length - 1}
                  className="p-1 rounded hover:bg-slate-100 text-slate-400 disabled:opacity-30 disabled:cursor-not-allowed"
                  aria-label="Move down"
                  data-testid={`move-down-${s.id}`}
                >
                  <ArrowDown className="h-3.5 w-3.5" />
                </button>
              </div>

              {/* Icon */}
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                s.enabled ? "bg-[#FF6B00]/10 text-[#FF6B00]" : "bg-slate-100 text-slate-400"
              }`}>
                <ServiceIcon icon={s.icon} />
              </div>

              {/* Label (editable) + ID */}
              <div className="flex-1 min-w-0 space-y-1">
                <Input
                  value={s.label}
                  onChange={(e) => renameLabel(s.id, e.target.value)}
                  placeholder="Customer-facing label"
                  className="h-8 text-sm font-semibold border-slate-200 focus-visible:ring-[#FF6B00]/40"
                  data-testid={`label-input-${s.id}`}
                />
                <p className="text-[11px] text-slate-400 font-mono">id: {s.id}</p>
              </div>

              {/* Status badge */}
              {s.enabled ? (
                <span
                  className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200"
                  data-testid={`status-badge-${s.id}`}
                >
                  <CheckCircle2 className="h-3 w-3" />
                  Active
                </span>
              ) : (
                <span
                  className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-bold bg-slate-100 text-slate-500 border border-slate-200"
                  data-testid={`status-badge-${s.id}`}
                >
                  <XCircle className="h-3 w-3" />
                  Disabled
                </span>
              )}

              {/* Toggle */}
              <Switch
                checked={s.enabled}
                onCheckedChange={() => toggleEnabled(s.id)}
                data-testid={`toggle-${s.id}`}
                className="data-[state=checked]:bg-[#FF6B00]"
              />
            </div>
          ))}
        </div>
      )}

      <div className="text-xs text-slate-500 bg-amber-50 border border-amber-200 rounded-lg p-3">
        <strong className="text-amber-700">Heads-up:</strong> Disabling a service type hides it from
        the customer booking page and rejects new bookings of that type. <strong>Existing orders
        keep working as normal.</strong> Pricing, payment (Razorpay/COD), and tracking are not
        affected.
      </div>
    </div>
  );
}
